import { FC, useEffect, useState } from "react";
import _ from "lodash";
import { useMutation } from "react-query";
import { useForm, FieldValues } from "react-hook-form";
import { JobsSearch } from "./JobsSearch";
import { JobsTable } from "./JobsTable";
import { useSetJobsState } from "../hooks/useSetJobsState";
import { fetchGetAllCompanies } from "../../organization-management/miscellaneous/fetchGetAllCompanies";
import { fetchParentCompanyLookup } from "../../organization-management/miscellaneous/fetchParentCompanyLookup";
import { fetchQueryJobTitle } from "../miscellaneous/fetchQueryJobTitle";
import { useJobsValue } from "../hooks/useJobsValue";
import { usePaginationTable } from "../../common/hooks/usePaginationTable";
import { useCompanyManagementSearchValue } from "../../company-management/hooks/useCompanyManagementSearchValue";
import { useSetCompanyManagementSearch } from "../../company-management/hooks/useSetCompanyManagementSearch";
import { defaultCompanyManagementSearchState } from "../../company-management/miscellaneous/companyManagementSearchAtom";

export const JobsManagement: FC = () => {
  const setJobs = useSetJobsState();
  const jobsValue = useJobsValue();

  const searchAtomState = useCompanyManagementSearchValue();
  const setSearchAtomState = useSetCompanyManagementSearch();
  const jobTabAtom = searchAtomState.jobTab;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
    reset,
  } = useForm<FieldValues>({
    defaultValues: {
      company: jobTabAtom.criteria.parentCompanyId,
      organization: jobTabAtom.criteria.companyId,
      jobTitle: jobTabAtom.criteria.title,
    },
  });

  const {
    page,
    rowsPerPage,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage
  } = usePaginationTable(jobsValue, jobTabAtom.page, jobTabAtom.pageSize);

  // Sync page/pageSize to atom
  useEffect(() => {
    setSearchAtomState((prev) => ({
      ...prev,
      jobTab: { ...prev.jobTab, page, pageSize: rowsPerPage },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const [jobsSearch, setJobsSearchLocal] = useState(jobTabAtom.criteria);

  const setJobsSearch = (newCriteria: any) => {
    setJobsSearchLocal(newCriteria);
    setSearchAtomState((prev) => ({
      ...prev,
      jobTab: { ...prev.jobTab, criteria: newCriteria, hasSearched: true },
    }));
  };

  const handleResetAtom = () => {
    setSearchAtomState((prev) => ({
      ...prev,
      jobTab: defaultCompanyManagementSearchState.jobTab,
    }));
  };

  const [companiesName, setCompaniesName] = useState([]);
  const [parentCompaniesName, setParentCompaniesName] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const mutationGetCompanies = useMutation(fetchGetAllCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companyName = _.map(res.data.data, (item) => {
          return { name: item.companyName, value: item.id };
        });
        setCompaniesName(companyName as any);
      }
    },
    onError: () => {},
  });

  const mutationGetParentCompanies = useMutation(fetchParentCompanyLookup, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const parentCompanyName = _.map(res.data, (item) => {
          return { name: item.companyName, value: item.id };
        });
        setParentCompaniesName(parentCompanyName as any);
      }
    },
    onError: () => {},
  });

  const mutateQueryJobs = useMutation(fetchQueryJobTitle, {
    onSuccess: (res) => {
      setJobs(res.data.data);
      setTotalRecords(res.data.totalRecords);
    },
    onError: () => {},
  });

  useEffect(() => {
    mutationGetCompanies.mutate();
    mutationGetParentCompanies.mutate();
  }, []);

  useEffect(() => {
    const payload = {
      parentCompanyId: jobsSearch.parentCompanyId,
      companyId: jobsSearch.companyId,
      title: jobsSearch.title,
      pageNumber: page,
      pageSize: rowsPerPage
    };
    mutateQueryJobs.mutate(payload);
  }, [JSON.stringify(jobsSearch), page, rowsPerPage]);

  return (
    <>
      <JobsSearch
        control={control}
        register={register}
        handleSubmit={handleSubmit}
        setValue={setValue}
        reset={reset}
        setJobsSearch={setJobsSearch}
        companies={companiesName}
        parentCompanies={parentCompaniesName}
        setPage={setPage}
        onAtomReset={handleResetAtom}
      />
      <JobsTable
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        totalRecords={totalRecords}
      />
    </>
  );
};
